import { useAuth } from "common";
import { FormikProvider, useFormik } from "formik";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Location,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { GetRef, Separator, Text, View, XStack, YStack } from "tamagui";
import { GradientButton } from "tamagui-config";

import { FormFieldContainer } from "../components/FormFieldContainer";
import { FormikInput } from "../components/FormikInput";
import { GlowCircle } from "../components/GlowCircle";
import { Input } from "../components/Input";
import useFieldValidationSchema from "../hooks/useFieldValidationSchema";
import { useShowToast } from "../hooks/useShowToast";

type ResetPasswordFormValues = {
  email: string;
  code: string;
  newPassword: string;
  repeatedNewPassword: string;
};

const ResetPassword = () => {
  const { forgotPasswordSubmit, isLoading } = useAuth();
  const { state } = useLocation() as Location<{ email: string } | undefined>;
  const [searchParams] = useSearchParams();
  const { resetPasswordValidationSchema } = useFieldValidationSchema();
  const navigate = useNavigate();
  const newPasswordInputRef = useRef<GetRef<typeof Input>>(null);
  const { showErrorToast } = useShowToast();
  const { t } = useTranslation();
  const email = searchParams.get("email") ?? state?.email;
  const code = searchParams.get("code");

  const parseError = (error: any) => {
    if (
      error.name === "CodeMismatchException" ||
      error.name === "ExpiredCodeException"
    ) {
      return t("resetPassword.invalidCodeError");
    }
    return t("resetPassword.unknownError");
  };

  const onSubmit = async ({ code, newPassword }: ResetPasswordFormValues) => {
    try {
      await forgotPasswordSubmit(email ?? "", code, newPassword);
      navigate("/reset-password-confirmation");
    } catch (error) {
      showErrorToast(parseError(error));
    }
  };

  const formik = useFormik<ResetPasswordFormValues>({
    initialValues: {
      email: email ?? "",
      code: code ?? "",
      newPassword: "",
      repeatedNewPassword: "",
    },
    enableReinitialize: true,
    validationSchema: resetPasswordValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit,
  });

  return (
    <View
      position="relative"
      minHeight="100vh"
      alignItems="center"
      justifyContent="center"
      backgroundColor="black"
    >
      <View position="absolute" width="100%" height="100%">
        <GlowCircle />
      </View>

      <YStack
        gap="$6"
        width={473}
        maxWidth="100%"
        padding="$7"
        backgroundColor="$transparentBorder"
        borderWidth={2}
        borderColor="$transparentBorder"
        borderRadius="$8"
      >
        <Text fontWeight="bold" color="$white12" textAlign="center">
          {t("resetPassword.title")}
        </Text>

        <Separator />

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <YStack gap="$3.5" padding="$3.5">
              <YStack gap="$2.5" paddingHorizontal="$5" paddingBottom="$3.5">
                <Text
                  fontSize="$7.5"
                  fontWeight="bold"
                  color="$white12"
                  textAlign="center"
                >
                  {t("resetPassword.formTitle")}
                </Text>
                <Text color="$white10" textAlign="center">
                  {t("resetPassword.formDescription")}
                </Text>
              </YStack>

              <FormFieldContainer
                required
                error={formik.touched.code && !!formik.errors.code}
                helperText={
                  formik.touched.code && formik.errors.code
                    ? formik.errors.code
                    : undefined
                }
              >
                <OTPInput
                  defaultValue={code ?? ""}
                  autoFocus
                  pattern={REGEXP_ONLY_DIGITS}
                  maxLength={6}
                  render={({ slots, isHovering }) => (
                    <XStack gap="$3.5">
                      {slots.map(
                        ({ char, placeholderChar, isActive }, index) => (
                          <View key={index} flexGrow={1} flexBasis={0}>
                            <Input // This Input component is just for display purposes
                              readOnly
                              width="100%"
                              height={64}
                              textAlign="center"
                              value={char ?? ""}
                              placeholder={placeholderChar ?? ""}
                              error={
                                formik.touched.code && !!formik.errors.code
                              }
                              hovered={isHovering}
                              focused={isActive}
                            />
                          </View>
                        ),
                      )}
                    </XStack>
                  )}
                  onChange={formik.handleChange("code")}
                  onBlur={formik.handleBlur("code")}
                  onComplete={() => newPasswordInputRef.current?.focus()}
                />
              </FormFieldContainer>

              {!formik.values.email && (
                <FormikInput
                  fieldName="email"
                  placeholder={t("resetPassword.emailPlaceholder")}
                  autoCapitalize="none"
                  autoComplete="email"
                  keyboardType="email-address"
                />
              )}
              <FormikInput
                ref={newPasswordInputRef}
                fieldName="newPassword"
                placeholder={t("resetPassword.newPasswordPlaceholder")}
                autoCapitalize="none"
                isPassword
              />
              <FormikInput
                fieldName="repeatedNewPassword"
                placeholder={t("resetPassword.repeatedNewPasswordPlaceholder")}
                autoCapitalize="none"
                isPassword
              />

              <GradientButton
                rounded
                width="100%"
                loading={isLoading}
                onPress={() => formik.handleSubmit()}
              >
                {t("resetPassword.submitButton")}
              </GradientButton>
            </YStack>
          </form>
        </FormikProvider>
      </YStack>
    </View>
  );
};

export default ResetPassword;
